/**

 * Copyright (c) 2025 Kalpit Patel

 *

 * Public Gallery – highlights from career expos, fairs, and events.

 */



import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';

import { useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {

  faChevronLeft,

  faChevronRight,

  faTimes,

  faImages,

  faCalendarAlt,

  faMapMarkerAlt,

  faCamera,

  faHandshake,

  faUsers,

  faRocket

} from '@fortawesome/free-solid-svg-icons';

import { fetchPublicGallery, getGalleryImageUrl } from '../api/galleryApi';

import { GALLERY_CATEGORIES } from '../constants/galleryCategories';

import { getGalleryEventKey } from '../utils/galleryEventUtils';

import GalleryEventCard, { GalleryFeaturedSlideshow } from '../components/GalleryEventCard';

import Aurora from '../components/Aurora';

import '../styles/GalleryPage.css';



const STATS = [

  { icon: faCamera, value: '50+', label: 'Event Moments' },

  { icon: faUsers, value: '500+', label: 'Attendees' },

  { icon: faHandshake, value: '5', label: 'Partner Organizations' },

  { icon: faRocket, value: '1+', label: 'Cities Reached' }

];



const normalizeItem = (item) => ({

  id: item.id,

  title: item.title,

  eventTitle: item.eventTitle || item.title,

  category: item.category,

  location: item.location,

  date: item.date,

  image: getGalleryImageUrl(item.imagePath),

  featured: item.featured,

  size: item.size || 'normal',

  displayOrder: item.displayOrder || 0

});



const groupGalleryItems = (items) => {

  const groups = new Map();



  items.forEach((item) => {

    const key = getGalleryEventKey(item);

    if (!groups.has(key)) {

      groups.set(key, {

        id: key,

        title: getGalleryEventKey(item),

        category: item.category,

        location: item.location,

        date: item.date,

        featured: item.featured,

        size: item.size,

        displayOrder: item.displayOrder,

        images: []

      });

    }



    const group = groups.get(key);

    group.images.push({

      id: item.id,

      url: item.image,

      title: item.title

    });

    if (item.featured) group.featured = true;

    group.displayOrder = Math.min(
      group.displayOrder ?? Number.MAX_SAFE_INTEGER,
      item.displayOrder ?? Number.MAX_SAFE_INTEGER
    );

    if (!group.location && item.location) group.location = item.location;

    if (!group.date && item.date) group.date = item.date;

  });



  return Array.from(groups.values()).sort((a, b) => {

    if (a.displayOrder === b.displayOrder) return 0;

    return (a.displayOrder || 0) - (b.displayOrder || 0);

  });

};



const flattenLightboxImages = (groups) =>

  groups.flatMap((group) =>

    group.images.map((image) => ({

      ...image,

      groupTitle: group.title,

      category: group.category,

      location: group.location,

      date: group.date

    }))

  );



function Gallery() {

  const navigate = useNavigate();

  const [galleryItems, setGalleryItems] = useState([]);

  const [loading, setLoading] = useState(true);

  const [activeCategory, setActiveCategory] = useState('all');

  const [lightboxIndex, setLightboxIndex] = useState(null);

  const highlightsRef = useRef(null);

  const scrollHighlights = useCallback((direction) => {
    const el = highlightsRef.current;
    if (!el) return;
    const step = Math.max(280, Math.floor(el.clientWidth * 0.85));
    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  }, []);



  useEffect(() => {

    let cancelled = false;



    const loadGallery = async () => {

      setLoading(true);

      try {

        const data = await fetchPublicGallery();

        if (!cancelled) {

          setGalleryItems((data || []).map(normalizeItem));

        }

      } catch {

        if (!cancelled) {

          setGalleryItems([]);

        }

      } finally {

        if (!cancelled) {

          setLoading(false);

        }

      }

    };



    loadGallery();

    return () => {

      cancelled = true;

    };

  }, []);



  const eventGroups = useMemo(() => groupGalleryItems(galleryItems), [galleryItems]);



  const featuredGroup = useMemo(() => {

    return eventGroups.find((group) => group.featured) || eventGroups[0] || null;

  }, [eventGroups]);



  const stats = useMemo(() => {

    const photoCount = galleryItems.length;

    const eventCount = eventGroups.length;

    if (photoCount === 0) return STATS;



    return STATS.map((stat, index) => {

      if (index === 0) {

        const count = photoCount || eventCount;

        return { ...stat, value: `${count}${count >= 50 ? '+' : ''}` };

      }

      return stat;

    });

  }, [galleryItems.length, eventGroups.length]);



  const filteredGroups = useMemo(() => {

    if (activeCategory === 'all') return eventGroups;

    return eventGroups.filter((group) => group.category === activeCategory);

  }, [activeCategory, eventGroups]);



  const lightboxImages = useMemo(

    () => flattenLightboxImages(filteredGroups),

    [filteredGroups]

  );



  const openLightbox = useCallback((groupIndex, imageIndex = 0) => {

    let offset = 0;

    for (let i = 0; i < groupIndex; i += 1) {

      offset += filteredGroups[i].images.length;

    }

    setLightboxIndex(offset + imageIndex);

  }, [filteredGroups]);



  const closeLightbox = useCallback(() => setLightboxIndex(null), []);



  const goPrev = useCallback(() => {

    setLightboxIndex((prev) =>

      prev === null ? null : (prev - 1 + lightboxImages.length) % lightboxImages.length

    );

  }, [lightboxImages.length]);



  const goNext = useCallback(() => {

    setLightboxIndex((prev) =>

      prev === null ? null : (prev + 1) % lightboxImages.length

    );

  }, [lightboxImages.length]);



  useEffect(() => {

    if (lightboxIndex === null) return undefined;



    const handleKey = (e) => {

      if (e.key === 'Escape') closeLightbox();

      if (e.key === 'ArrowLeft') goPrev();

      if (e.key === 'ArrowRight') goNext();

    };



    document.body.style.overflow = 'hidden';

    window.addEventListener('keydown', handleKey);

    return () => {

      document.body.style.overflow = '';

      window.removeEventListener('keydown', handleKey);

    };

  }, [lightboxIndex, closeLightbox, goPrev, goNext]);



  const activeItem = lightboxIndex !== null ? lightboxImages[lightboxIndex] : null;



  return (

    <div className="gallery-page">

      <header className="gallery-page-header">

        <button

          type="button"

          className="gallery-page-back"

          onClick={() => navigate('/')}

          aria-label="Back to home"

        >

          <FontAwesomeIcon icon={faChevronLeft} />

        </button>

        <div className="gallery-page-header-inner">

          <div className="gallery-header-badge">

            <FontAwesomeIcon icon={faImages} />

            <span>EVENT HIGHLIGHTS</span>

          </div>

          <h1 className="gallery-page-title">Gallery</h1>

          <p className="gallery-page-subtitle">

            Moments from career expos, fairs, workshops, and networking events across Kenya

          </p>

        </div>

      </header>



      <div className="gallery-page-content">

        <section className="gallery-stats" aria-label="Gallery statistics">

          {stats.map((stat) => (

            <div key={stat.label} className="gallery-stat-card">

              <div className="gallery-stat-icon">

                <FontAwesomeIcon icon={stat.icon} />

              </div>

              <div className="gallery-stat-value">{stat.value}</div>

              <div className="gallery-stat-label">{stat.label}</div>

            </div>

          ))}

        </section>



        {featuredGroup && featuredGroup.images[0] && (

          <section className="gallery-featured" aria-label="Featured highlight">

            <GalleryFeaturedSlideshow group={featuredGroup} />

            <div className="gallery-featured-content">

              <span className="gallery-featured-tag">Featured</span>

              <h2>{featuredGroup.title}</h2>

              <p>

                Relive the energy of our flagship career fair — where students, employers,

                and communities connect to shape brighter futures.

              </p>

              <div className="gallery-featured-meta">

                {featuredGroup.location && (

                  <span>

                    <FontAwesomeIcon icon={faMapMarkerAlt} />

                    {featuredGroup.location}

                  </span>

                )}

                {featuredGroup.date && (

                  <span>

                    <FontAwesomeIcon icon={faCalendarAlt} />

                    {featuredGroup.date}

                  </span>

                )}

              </div>

            </div>

          </section>

        )}



        <div className="gallery-filters" role="tablist" aria-label="Filter gallery by category">

          {GALLERY_CATEGORIES.map((cat) => (

            <button

              key={cat.id}

              type="button"

              role="tab"

              aria-selected={activeCategory === cat.id}

              className={`gallery-filter-btn${activeCategory === cat.id ? ' active' : ''}`}

              onClick={() => {

                setActiveCategory(cat.id);

                setLightboxIndex(null);

              }}

            >

              {cat.label}

            </button>

          ))}

        </div>



        {loading ? (

          <p className="gallery-empty">Loading gallery...</p>

        ) : (

          <div className="gallery-highlights-carousel">

            <button
              type="button"
              className="gallery-carousel-btn"
              onClick={() => scrollHighlights(-1)}
              aria-label="Previous highlights"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <section
              className="gallery-grid"
              ref={highlightsRef}
              aria-label="Photo gallery"
            >
              {filteredGroups.map((group, groupIndex) => (
                <GalleryEventCard
                  key={group.id}
                  group={group}
                  groupIndex={groupIndex}
                  onOpenLightbox={openLightbox}
                />
              ))}
            </section>

            <button
              type="button"
              className="gallery-carousel-btn"
              onClick={() => scrollHighlights(1)}
              aria-label="Next highlights"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>

          </div>

        )}



        {!loading && filteredGroups.length === 0 && (

          <p className="gallery-empty">No photos in this category yet. Check back soon!</p>

        )}



        <section className="gallery-bottom-cta">

          <div className="gallery-bottom-cta-bg" aria-hidden="true">

            <Aurora

              colorStops={['#ff8c42', '#ffa726', '#14b8a6']}

              blend={0.5}

              amplitude={1.0}

              speed={0.5}

            />

          </div>

          <div className="gallery-bottom-cta-content">

            <h2>Be Part of Our Next Event</h2>

            <p>

              Join thousands of job seekers, students, and employers at upcoming expos and career fairs.

            </p>

            <div className="gallery-bottom-cta-actions">

              <button

                type="button"

                className="gallery-cta-btn"

                onClick={() => navigate('/events')}

              >

                View Upcoming Events

              </button>

            </div>

          </div>

        </section>

      </div>



      {activeItem && (

        <div

          className="gallery-lightbox"

          role="dialog"

          aria-modal="true"

          aria-label={activeItem.groupTitle}

          onClick={closeLightbox}

        >

          <button

            type="button"

            className="gallery-lightbox-close"

            onClick={closeLightbox}

            aria-label="Close"

          >

            <FontAwesomeIcon icon={faTimes} />

          </button>

          <button

            type="button"

            className="gallery-lightbox-nav gallery-lightbox-prev"

            onClick={(e) => {

              e.stopPropagation();

              goPrev();

            }}

            aria-label="Previous image"

          >

            <FontAwesomeIcon icon={faChevronLeft} />

          </button>

          <div className="gallery-lightbox-content" onClick={(e) => e.stopPropagation()}>

            <img src={activeItem.url} alt={activeItem.groupTitle} className="gallery-lightbox-image" />

            <div className="gallery-lightbox-caption">

              <span className="gallery-lightbox-category">

                {GALLERY_CATEGORIES.find((c) => c.id === activeItem.category)?.label}

              </span>

              <h3>{activeItem.groupTitle}</h3>

              <div className="gallery-lightbox-meta">

                {activeItem.location && (

                  <span>

                    <FontAwesomeIcon icon={faMapMarkerAlt} />

                    {activeItem.location}

                  </span>

                )}

                {activeItem.date && (

                  <span>

                    <FontAwesomeIcon icon={faCalendarAlt} />

                    {activeItem.date}

                  </span>

                )}

              </div>

              <p className="gallery-lightbox-counter">

                {lightboxIndex + 1} / {lightboxImages.length}

              </p>

            </div>

          </div>

          <button

            type="button"

            className="gallery-lightbox-nav gallery-lightbox-next"

            onClick={(e) => {

              e.stopPropagation();

              goNext();

            }}

            aria-label="Next image"

          >

            <FontAwesomeIcon icon={faChevronRight} />

          </button>

        </div>

      )}

    </div>

  );

}



export default Gallery;


